import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  ViewChild
} from '@angular/core';
import { ChartConfiguration, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { ChartColor } from '../../../enums/chart-color.enum';
import { Currency } from '../../../enums/currency.enum';
import { Balance } from '../../../models/balance.model';
import { LineChartData } from '../../../models/line-chart-data.model';
import { separateThousands } from '../../utils/separate-thousands.utils';

@Component({
  selector: 'lns-line-chart',
  templateUrl: './line-chart.component.html',
  styleUrls: ['./line-chart.component.scss']
})
export class LineChartComponent implements AfterViewInit {
  chartData: LineChartData = {
    labels: [],
    data: [],
    currency: Currency.Eur
  };
  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;
  @ViewChild('lineChart') canvas: ElementRef = new ElementRef<any>(
    HTMLCanvasElement
  );

  @Input() set balance(balance: Balance | undefined) {
    const labels: any = [];
    const data: any = [];

    if (balance) {
      balance.dailyChanges?.map(dailyChange => {
        labels.push(dailyChange.date);
        data.push(dailyChange.dailyBalance);
      });

      this.chartData = {
        labels,
        data,
        currency: balance.currency
      };
    }
  }
  lineChartData!: ChartConfiguration['data'];
  @Input() color: ChartColor | undefined;
  @Input() height = 135;

  chartType: ChartType = 'line';

  chartLegend = false;
  lineChartColors: any[] = [
    {
      borderWidth: 5,
      pointBackgroundColor: 'white',
      pointBorderWidth: 3
    }
  ];

  chartOptions: ChartConfiguration['options'] = {
    responsive: true,
    layout: {
      padding: {
        top: 8,
        right: 8,
        left: 8,
        bottom: 8
      }
    },
    elements: {
      line: {
        tension: 0.5
      },
      point: {
        radius: ctx => {
          if (
            ctx.dataIndex &&
            ctx.dataIndex + 1 === ctx.dataset?.data?.length
          ) {
            return 6;
          }
          return 0;
        }
      }
    },
    maintainAspectRatio: false,
    hover: {
      mode: undefined
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: 'point',
        intersect: false,
        bodyAlign: 'center',
        enabled: true,
        bodyFont: {
          weight: 700
        },
        displayColors: false,
        backgroundColor: '#D4AD67',

        callbacks: {
          title(): string | string[] {
            return '';
          },
          label: (tooltipItem: any) => {
            return this.editChartTooltip(tooltipItem);
          }
        },
        filter(item: any, data: any): boolean {
          return !!(
            item.dataIndex && item.dataIndex + 1 === item.dataset?.data.length
          );
        }
      }
    },
    scales: {
      x: {
        display: false,
        grid: {
          display: false
        }
      },
      y: {
        display: false,
        grid: {
          display: false
        }
      }
    }
  };
  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.lineChartColors = [
      {
        borderColor: this.color,
        backgroundColor: this.createChartGradient()
      }
    ];

    this.lineChartData = {
      datasets: [
        {
          data: this.chartData.data,
          backgroundColor: this.createChartGradient(),
          borderColor: this.color,
          borderWidth: 5,
          pointBackgroundColor: 'white',
          pointBorderWidth: 3,
          pointBorderColor: this.color,
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgba(148,159,177,0.8)',
          fill: 'origin'
        }
      ],
      labels: this.chartData.labels
    };
    this.cdr.detectChanges();
  }

  createChartGradient(): string | string[] {
    const gradient = this.canvas.nativeElement
      .getContext('2d')
      .createLinearGradient(0, 0, 0, 115);

    gradient.addColorStop(0, '#cfe4ff');
    gradient.addColorStop(1, 'white');

    return gradient;
  }

  editChartTooltip(value: any): string {
    const balance = Number(value!.raw).toFixed(2);
    const [fixed, decimal] = balance.toString().split('.');
    return `${separateThousands(fixed, '.')},${decimal} ${
      this.chartData.currency
    }`;
  }
}
